import React, { useState, useEffect } from 'react';
import { PersonDetailData } from '../index';
import { Camera, Star, AlertTriangle, X, Loader2 } from 'lucide-react';
import { api } from '../../../services/api';
import PhotoActionModal from '../../PhotoActionModal';

import PhotoCaptureModalEnhanced, { PhotoCaptureResult as PhotoResult } from '../../PhotoCaptureModalEnhanced';
import PhotoIPCameraModal from '../../PhotoIPCameraModal';
import toast from 'react-hot-toast';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { cn } from '../../../utils/cn';
import { useConfirm } from '../../ui/ConfirmDialog';
import { appendIdentityPhoto } from '../../../services/identityPhotoUpload';

interface IdentityPhotosTabProps {
  person: PersonDetailData;
  onRefresh: () => void;
  onPhotoClick?: (photoUrl: string) => void;
  readOnly?: boolean;
  darkMode?: boolean;
  triggerAdd?: boolean;
  onTriggerAddConsumed?: () => void;
}

export const IdentityPhotosTab: React.FC<IdentityPhotosTabProps> = ({ person, onRefresh, onPhotoClick, readOnly = false, darkMode = false, triggerAdd = false, onTriggerAddConsumed }) => {
  const confirm = useConfirm();
  const [uploading, setUploading] = useState(false);
  const [loadedPhotos, setLoadedPhotos] = useState<Set<string>>(new Set());
  const markPhotoLoaded = (id: string) => setLoadedPhotos(prev => new Set(prev).add(id));
  const [showActionModal, setShowActionModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  // "Câmera IP" opens the live capture (PhotoIPCameraModal), not PhotoCaptureModalEnhanced's
  // IP tab. The tab shows an empty frame and "Selecione uma câmera e clique em Capturar" until
  // the operator picks from a dropdown; the live capture shows the feed and captures on click,
  // which is what every other photo surface in the app already does.
  const [showIPCameraModal, setShowIPCameraModal] = useState(false);
  const [showEditIPCameraModal, setShowEditIPCameraModal] = useState(false);

  // "Fora do padrão" flow
  const [flaggedPhoto, setFlaggedPhoto] = useState<{ id: string; isPrimary: boolean } | null>(null);
  const [showFlagConfirmModal, setShowFlagConfirmModal] = useState(false);
  const [photoModalTab, setPhotoModalTab] = useState<0 | 1>(0);

  useEffect(() => {
    if (triggerAdd && !readOnly) {
      setShowActionModal(true);
      onTriggerAddConsumed?.();
    }
  }, [triggerAdd]); // eslint-disable-line

  const openWithTab = (tab: 0 | 1) => {
    setShowActionModal(false);
    setPhotoModalTab(tab);
    setShowPhotoModal(true);
  };
  const [settingPrimary, setSettingPrimary] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Photo editing state
  const [editingPhoto, setEditingPhoto] = useState<{ id: string; url: string } | null>(null);
  const [showEditActionModal, setShowEditActionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPhotoTab, setEditPhotoTab] = useState<0 | 1>(0);

  const handlePhotoCapture = async (result: PhotoResult) => {
    setUploading(true);
    try {
      const formData = new FormData();
      appendIdentityPhoto(formData, result);

      // Add customer_id if available
      if (person.customer?.id) {
        formData.append('customer_id', person.customer.id);
      }

      await api.post(`/people/${person.id}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Foto enviada com sucesso!');
      setShowPhotoModal(false);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to upload photo:', err);
      toast.error(err.response?.data?.error || 'Erro ao enviar foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!await confirm({ message: 'Tem certeza que deseja excluir esta foto?', variant: 'danger', confirmLabel: 'Excluir' })) return;

    setDeleting(photoId);
    try {
      await api.delete(`/people/${person.id}/photos/${photoId}`);
      toast.success('Foto excluída com sucesso!');
      onRefresh();
    } catch (err: any) {
      console.error('Failed to delete photo:', err);
      toast.error(err.response?.data?.error || 'Erro ao excluir foto');
    } finally {
      setDeleting(null);
    }
  };

  const handleSetPrimary = async (photoId: string) => {
    setSettingPrimary(photoId);
    try {
      await api.put(`/identity-photos/${photoId}/set-primary`);
      toast.success('Foto principal definida com sucesso!');
      onRefresh();
    } catch (err: any) {
      console.error('Failed to set primary photo:', err);
      toast.error(err.response?.data?.error || 'Erro ao definir foto principal');
    } finally {
      setSettingPrimary(null);
    }
  };

  const handleEditPhoto = (photoId: string, photoUrl: string) => {
    setEditingPhoto({ id: photoId, url: photoUrl });
    setEditPhotoTab(0);
    setShowEditModal(true);
  };

  const openEditWithTab = (tab: 0 | 1) => {
    setEditPhotoTab(tab);
    setShowEditActionModal(false);
    setShowEditModal(true);
  };

  const handleSaveEditedPhoto = async (result: PhotoResult) => {
    if (!editingPhoto) return;

    setUploading(true);
    try {
      const formData = new FormData();
      appendIdentityPhoto(formData, result);

      if (person.customer?.id) {
        formData.append('customer_id', person.customer.id);
      }

      // Delete the old photo and upload the new one
      await api.delete(`/people/${person.id}/photos/${editingPhoto.id}`);
      await api.post(`/people/${person.id}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Foto editada com sucesso!');
      setShowEditModal(false);
      setEditingPhoto(null);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to update photo:', err);
      toast.error(err.response?.data?.error || 'Erro ao editar foto');
    } finally {
      setUploading(false);
    }
  };

  const openFlagFlow = (photoId: string, isPrimary: boolean) => {
    setFlaggedPhoto({ id: photoId, isPrimary });
    setShowFlagConfirmModal(true);
  };

  const confirmFlagFlow = async () => {
    setShowFlagConfirmModal(false);
    setUploading(true);
    try {
      const imgResponse = await fetch('/foto-fora-padrao.jpeg');
      const blob = await imgResponse.blob();

      const formData = new FormData();
      formData.append('photo', blob, 'foto-fora-padrao.jpg');
      if (person.customer?.id) {
        formData.append('customer_id', person.customer.id);
      }

      const uploadRes = await api.post(`/people/${person.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newPhotoId = uploadRes.data?.id || uploadRes.data?.photo?.id;
      if (newPhotoId) {
        await api.put(`/identity-photos/${newPhotoId}/set-primary`);
      }

      toast.success('Foto marcada como fora do padrão!');
      setFlaggedPhoto(null);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to upload flag photo:', err);
      toast.error(err.response?.data?.error || 'Erro ao marcar foto');
    } finally {
      setUploading(false);
    }
  };

  const cancelFlagFlow = () => {
    setShowFlagConfirmModal(false);
    setFlaggedPhoto(null);
  };

  // Filter out document photos (photo_type starts with "document") - only show identity/facial photos
  const allPhotos = person.identity_photos || [];
  const photos = allPhotos.filter(p => !p.photo_type?.startsWith('document'));
  const primaryPhoto = photos.find(p => p.is_primary);
  const otherPhotos = photos.filter(p => !p.is_primary);

  return (
    <div className="space-y-6">
      {/* Two Column Layout */}
      <div className="flex gap-6">
        {/* Left Column - Primary Photo / Add Photo */}
        <div className="w-52 flex-shrink-0">
          <div className="flex flex-col items-center">
            {/* Photo Container */}
            <div className="relative">
              {primaryPhoto ? (
                <div className="relative group">
                  {!loadedPhotos.has(primaryPhoto.id) && (
                    <div className="absolute inset-0 rounded-xl bg-gray-100 border-4 border-gray-200 flex items-center justify-center z-10">
                      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                    </div>
                  )}
                  <img
                    src={getAbsoluteImageUrl(primaryPhoto.photo_url)}
                    alt="Foto principal"
                    className={`w-32 h-40 rounded-xl object-cover border-4 border-gray-200 cursor-pointer transition-transform group-hover:scale-105${!loadedPhotos.has(primaryPhoto.id) ? ' opacity-0' : ''}`}
                    onClick={() => onPhotoClick?.(primaryPhoto.photo_url)}
                    onLoad={() => markPhotoLoaded(primaryPhoto.id)}
                    onError={() => markPhotoLoaded(primaryPhoto.id)}
                  />
                  {/* Primary badge */}
                  <div className="absolute bottom-1 right-1 bg-yellow-500 text-white p-1.5 rounded-full shadow-md">
                    <Star className="h-3.5 w-3.5 fill-current" />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowActionModal(true)}
                  disabled={uploading}
                  className="w-32 h-40 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center gap-2 hover:border-gray-400 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="h-8 w-8 text-gray-400" />
                  <span className="text-xs text-gray-500 text-center px-2">Adicionar Foto</span>
                </button>
              )}
            </div>

            {/* Title */}
            <p className={cn(
              "mt-3 text-xs font-medium text-center",
              darkMode ? "text-gray-400" : "text-gray-600"
            )}>
              {primaryPhoto ? 'Foto Principal' : 'Foto de Identificação'}
            </p>

            {/* Face Detection Status */}
            {primaryPhoto && (
              <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                <span>Foto principal definida</span>
              </div>
            )}

            {/* Actions - Only show when not readOnly */}
            {!readOnly && (
              <div className="mt-3 flex flex-col gap-1.5 w-full">
                {primaryPhoto && (
                  <button
                    type="button"
                    onClick={() => handleEditPhoto(primaryPhoto.id, primaryPhoto.photo_url)}
                    disabled={uploading}
                    className={cn(
                      "w-full px-3 py-1.5 text-xs font-bold tracking-wide border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                      darkMode
                        ? "text-blue-300 bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20"
                        : "text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                    )}
                  >
                    ATUALIZAR FOTO
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowActionModal(true)}
                  disabled={uploading}
                  className={cn(
                    "w-full px-3 py-1.5 text-xs font-bold tracking-wide border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                    darkMode
                      ? "text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700"
                      : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
                  )}
                >
                  {uploading ? 'ENVIANDO...' : 'NOVA FOTO'}
                </button>
                {primaryPhoto && (
                  <button
                    type="button"
                    onClick={() => handleDeletePhoto(primaryPhoto.id)}
                    disabled={deleting === primaryPhoto.id}
                    className={cn(
                      "w-full px-3 py-1.5 text-xs font-bold tracking-wide border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                      darkMode
                        ? "text-red-400 bg-red-500/10 border-red-500/30 hover:bg-red-500/20"
                        : "text-red-600 bg-red-50 border-red-200 hover:bg-red-100"
                    )}
                  >
                    EXCLUIR
                  </button>
                )}
              </div>
            )}

            {/* FORA DO PADRÃO - visible to admins/managers */}
            {!readOnly && primaryPhoto && (
              <button
                type="button"
                onClick={() => openFlagFlow(primaryPhoto.id, true)}
                className="mt-1 w-full px-3 py-1.5 text-xs font-bold tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
              >
                FORA DO PADRÃO
              </button>
            )}
          </div>
        </div>

        {/* Right Column - Other Photos Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className={cn(
              "text-sm font-medium",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              Outras Fotos
            </h3>
            <span className={cn(
              "text-xs",
              darkMode ? "text-gray-400" : "text-gray-500"
            )}>
              {otherPhotos.length} foto(s)
            </span>
          </div>

          {otherPhotos.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {otherPhotos.map((photo) => {
                const uploadDate = new Date(photo.created_at).toLocaleDateString('pt-BR');
                const isSettingPrimary = settingPrimary === photo.id;
                const isDeleting = deleting === photo.id;

                return (
                  <div
                    key={photo.id}
                    className="relative group"
                  >
                    {/* Photo */}
                    <div className="relative">
                      {!loadedPhotos.has(photo.id) && (
                        <div className="absolute inset-0 rounded-xl bg-gray-100 border-2 border-gray-200 flex items-center justify-center z-10 aspect-[4/5]">
                          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                        </div>
                      )}
                      <img
                        src={getAbsoluteImageUrl(photo.photo_url)}
                        alt="Foto de identificação"
                        className={`w-full aspect-[4/5] rounded-xl object-cover border-2 border-gray-200 cursor-pointer transition-transform group-hover:scale-[1.02]${!loadedPhotos.has(photo.id) ? ' opacity-0' : ''}`}
                        onClick={() => onPhotoClick?.(photo.photo_url)}
                        onLoad={() => markPhotoLoaded(photo.id)}
                        onError={() => markPhotoLoaded(photo.id)}
                      />

                        {/* Zoom overlay */}
                      <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
                    </div>

                    {/* Upload Date */}
                    <p className={cn(
                      "mt-1 text-xs text-center",
                      darkMode ? "text-gray-400" : "text-gray-500"
                    )}>
                      {uploadDate}
                    </p>

                    {/* Persistent action buttons */}
                    {!readOnly && (
                      <div className="mt-1 flex flex-col gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSetPrimary(photo.id); }}
                          disabled={isSettingPrimary}
                          className={cn(
                            "w-full flex items-center justify-center px-2 py-1 text-xs font-bold tracking-wide rounded-lg border transition-colors disabled:opacity-50",
                            darkMode
                              ? "bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                              : "bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200"
                          )}
                        >
                          {isSettingPrimary
                            ? <div className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            : 'DEFINIR PRINCIPAL'
                          }
                        </button>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditPhoto(photo.id, photo.photo_url); }}
                            className={cn(
                              "flex-1 flex items-center justify-center px-2 py-1 text-xs font-bold tracking-wide rounded-lg border transition-colors",
                              darkMode
                                ? "bg-gray-800 hover:bg-gray-700 text-gray-400 border-gray-600"
                                : "bg-white hover:bg-gray-50 text-gray-600 border-gray-300"
                            )}
                          >
                            SUBSTITUIR
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                            disabled={isDeleting}
                            className={cn(
                              "flex-1 flex items-center justify-center px-2 py-1 text-xs font-bold tracking-wide rounded-lg border transition-colors disabled:opacity-50",
                              darkMode
                                ? "bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30"
                                : "bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
                            )}
                          >
                            {isDeleting
                              ? <div className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              : 'EXCLUIR'
                            }
                          </button>
                        </div>
                      </div>
                    )}

                    {/* FORA DO PADRÃO */}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => openFlagFlow(photo.id, false)}
                        className="mt-1 w-full flex items-center justify-center px-2 py-1 text-xs font-bold tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                      >
                        FORA DO PADRÃO
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={cn(
              "flex flex-col items-center justify-center py-8 rounded-xl border-2 border-dashed",
              darkMode
                ? "text-gray-500 bg-gray-800 border-gray-700"
                : "text-gray-400 bg-gray-50 border-gray-200"
            )}>
              <Camera className="w-8 h-8 mb-2" />
              <p className="text-sm">Nenhuma foto adicional</p>
              <p className="text-xs mt-1">Clique em "Nova Foto" para adicionar</p>
            </div>
          )}

          {/* Info Box */}
          <div className={cn(
            "mt-4 p-3 rounded-lg border",
            darkMode ? "bg-blue-900/20 border-blue-800" : "bg-blue-50 border-blue-100"
          )}>
            <p className={cn(
              "text-xs",
              darkMode ? "text-blue-300" : "text-blue-800"
            )}>
              <strong>Dica:</strong> Clique na estrela para definir uma foto como principal.
              A foto principal e usada para reconhecimento facial nos dispositivos de acesso.
            </p>
          </div>
        </div>
      </div>

      {/* Method picker */}
      <PhotoActionModal
        open={showActionModal}
        onClose={() => setShowActionModal(false)}
        onUpload={() => openWithTab(0)}
        onWebcam={() => openWithTab(1)}
        onIPCamera={() => { setShowActionModal(false); setShowIPCameraModal(true); }}
        title="Nova Foto de Identificação"
        darkMode={darkMode}
      />

      {/* Unified editor */}
      <PhotoCaptureModalEnhanced
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onConfirm={(result) => { handlePhotoCapture(result); setShowPhotoModal(false); }}
        title="Nova Foto de Identificação"
        cropShape="rect"
        aspectRatio={4 / 5}
        customerId={person.customer?.id}
        initialTab={photoModalTab}
        hideIPCamera
      />

      {/* Live capture — the same component the registration forms and the lightbox use. */}
      {showIPCameraModal && (
        <PhotoIPCameraModal
          open={showIPCameraModal}
          onClose={() => setShowIPCameraModal(false)}
          onConfirm={(result) => { handlePhotoCapture(result); setShowIPCameraModal(false); }}
          title="Nova Foto de Identificação"
          customerId={person.customer?.id}
          filterCategory="captura_facial"
          cropShape="rect"
          aspectRatio={4 / 5}
          darkMode={darkMode}
        />
      )}

      {/* Edit photo — method picker */}
      {editingPhoto && (
        <PhotoActionModal
          open={showEditActionModal}
          onClose={() => { setShowEditActionModal(false); setEditingPhoto(null); }}
          onUpload={() => openEditWithTab(0)}
          onWebcam={() => openEditWithTab(1)}
          onIPCamera={() => { setShowEditActionModal(false); setShowEditIPCameraModal(true); }}
          title="Atualizar Foto de Identificação"
          darkMode={darkMode}
        />
      )}

      {/* Edit photo — capture modal */}
      {editingPhoto && (
        <PhotoCaptureModalEnhanced
          open={showEditModal}
          onClose={() => { setShowEditModal(false); setEditingPhoto(null); }}
          onConfirm={handleSaveEditedPhoto}
          title="Atualizar Foto de Identificação"
          cropShape="rect"
          aspectRatio={4 / 5}
          customerId={person.customer?.id}
          existingPhoto={getAbsoluteImageUrl(editingPhoto.url)}
          initialTab={editPhotoTab}
          hideIPCamera
        />
      )}

      {/* Edit photo — live capture */}
      {editingPhoto && showEditIPCameraModal && (
        <PhotoIPCameraModal
          open={showEditIPCameraModal}
          onClose={() => { setShowEditIPCameraModal(false); setEditingPhoto(null); }}
          onConfirm={(result) => { handleSaveEditedPhoto(result); setShowEditIPCameraModal(false); }}
          title="Atualizar Foto de Identificação"
          customerId={person.customer?.id}
          filterCategory="captura_facial"
          cropShape="rect"
          aspectRatio={4 / 5}
          darkMode={darkMode}
        />
      )}

      {/* FORA DO PADRÃO — Confirmation Modal */}
      {showFlagConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={cancelFlagFlow} />
          <div className={cn(
            "relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden",
            darkMode ? "bg-gray-900" : "bg-white"
          )}>
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-200 bg-amber-50">
              <div className="p-2 bg-amber-100 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-amber-900 tracking-wide">FOTO FORA DO PADRÃO</h3>
                <p className="text-xs text-amber-700">Identificação facial comprometida</p>
              </div>
              <button onClick={cancelFlagFlow} className="ml-auto p-1.5 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-3">
              <p className={cn("text-sm", darkMode ? "text-gray-300" : "text-gray-700")}>
                Deseja registrar uma nova foto como principal agora?
              </p>
              <img
                src="/foto-fora-padrao.jpeg"
                alt="Padrão de foto de identificação"
                className="w-full rounded-xl object-contain"
              />
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={cancelFlagFlow}
                className={cn(
                  "flex-1 py-2.5 text-sm font-medium rounded-xl border transition-colors",
                  darkMode ? "text-gray-300 border-gray-600 hover:bg-gray-800" : "text-gray-700 border-gray-300 hover:bg-gray-50"
                )}
              >
                Cancelar
              </button>
              <button
                onClick={confirmFlagFlow}
                className="flex-1 py-2.5 text-sm font-bold tracking-wide text-white bg-amber-500 hover:bg-amber-600 rounded-xl transition-colors"
              >
                CONFIRMAR
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
